import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import type { Action, Battle, BattleActor, BattleHint, BattleSkill, BattleStatus } from '@mezha/api'
import { useBattleQuery, useChooseMutation, useForfeitMutation } from '../../api/endpoints/battles'
import { useAppSelector } from '../../app/store'
import { useCommand } from '../../lib/useCommand'
import { countdown, useRefetchAt, useServerNow } from '../../lib/time'
import { useWide } from '../../lib/useSize'
import { CombatAction } from './scene/CombatAction'
import { CombatTooltip } from './scene/CombatTooltip'
import { useShellMode } from '../../shell/ShellContext'
import { Emblem, type EmblemName } from '../../ui/brand/Emblems'
import { Failure, Loading } from '../../ui/State'
import { Gauge, PlankButton, SealBar, Sheet, cx, type SealState } from '../../ui'
import { loadDraft, saveDraft, type Draft } from './draft'
import { BattleResult } from './BattleResult'
import { ACTION_RU, CHARM_MANA, ONSLAUGHT_FOCUS, REST_FOCUS, REST_MANA, STATUS_RU, STATUS_TEXT, TWILIGHT_PCT, ZONE_INTO, ZONE_RU, ZONE_TITLE, type BlowZone } from './words'
import { BLOCK_RU, KIND_EMBLEM, KIND_RU, costText, roundsText } from '../skills/words'
import { FullLog } from './RoundLog'
import { BattleScene } from './scene/BattleScene'
import { usePlayback, useReducedCombatMotion } from './scene/usePlayback'
import './battle.css'
import './scene/scene.css'

const ZONES: BlowZone[] = ['head', 'body', 'legs']

function GroupRules({ battle }: { battle: Battle }) {
  const rules = battle.group_rules
  if (battle.kind !== 'pve' || rules?.version !== 1 || rules.hero_id !== battle.you || battle.actors.filter(a => a.side !== battle.your_side).length < 2) return null
  return <CombatTooltip className="actnote combat-group-rules" name="Бой с группой"
    description={`В раунд атакуют не больше ${rules.frontage} врагов, остальные ждут. Каждый ваш удар также задевает остальных живых врагов на ${rules.cleave_pct}% урона с учётом защиты каждой цели, без дополнительных статусов и затрат.`}
    note={`Если в начале раунда живы хотя бы два врага, их прошедший по вам урон усиливается на ${rules.incoming_pct}%. Когда остаётся один враг, усиление прекращается.`}>
    {() => <span>Бой с группой · размах {rules.cleave_pct}% · натиск +{rules.incoming_pct}% ⓘ</span>}
  </CombatTooltip>
}

/** What each common action does, as the note under the action row says it. */
const ACTION_NOTE: Record<Action, (twilight: boolean) => string> = {
  strike: () => 'обычный удар оружием; после раунда +1 напор и +2 маны.',
  onslaught: () => `урон ×1,35 за ${ONSLAUGHT_FOCUS} напора; ваша закрытая зона пропускает 50%.`,
  charm: () => `за ${CHARM_MANA} маны: удар, урон по вам ×0,8 и оберег на раунд.`,
  rest: twilight => `без удара: +${REST_FOCUS} напора${twilight ? ' (в Сумерки без маны)' : `, +${REST_MANA} маны`}; закрытая зона пропускает 15%.`,
  skill: () => '',
}

/** Does the chosen action strike this round? */
function strikes(action: Action, k: BattleSkill | null): boolean {
  if (action === 'rest') return false
  if (action === 'skill') return Boolean(k && k.blow !== 'none')
  return true
}

/** A skill's facts for the note: kind, price, cooldown, once a battle, zone. */
function skillFacts(k: BattleSkill): string {
  const f = [KIND_RU[k.kind], costText(k.cost_focus, k.cost_mana)]
  if (k.once) f.push('раз за бой')
  if (k.zone) f.push(`только ${ZONE_INTO[k.zone as BlowZone]}`)
  return f.filter(Boolean).join(' · ')
}

/** `/play/battle/:id`: a running battle in the combat mode of the shell, or its result. */
export function BattleScreen() {
  const { id = '' } = useParams()
  const battle = useBattleQuery(id, { skip: !id })
  const b = battle.currentData
  if (battle.isLoading || (!b && battle.isFetching)) return <CombatFrame><div className="battle__state"><Loading text="Открываем бой…" /></div></CombatFrame>
  if (!b) return <CombatFrame><div className="battle__state"><Failure error={battle.error} retry={() => void battle.refetch()} what="Бой" /></div></CombatFrame>
  return <CombatSession key={b.id} battle={b} refetch={() => void battle.refetch()} failed={battle.isError} />
}

function CombatSession(props: { battle: Battle; refetch: () => void; failed: boolean }) {
  const reduced = useReducedCombatMotion(useAppSelector(s => s.ui.lessMotion))
  const playback = usePlayback(props.battle, reduced)
  if ((props.battle.status !== 'active' || props.battle.result) && !playback.play) return <BattleResult battle={props.battle} />
  return <Combat {...props} playback={playback} reduced={reduced} />
}

function CombatFrame({ children }: { children: ReactNode }) {
  useShellMode('combat')
  return <div className="battle">{children}</div>
}

function Combat({ battle: b, refetch, failed, playback, reduced }: { battle: Battle; refetch: () => void; failed: boolean; playback: ReturnType<typeof usePlayback>; reduced: boolean }) {
  useShellMode('combat')
  const wide = useWide()
  const now = useServerNow(250)
  const connection = useAppSelector(s => s.ui.connection)
  const [choose, choosing] = useChooseMutation()
  const [forfeit, forfeiting] = useForfeitMutation()
  const sending = useRef(false)
  // Refusals that mean «the snapshot moved on» re-read the battle.
  const cmd = useCommand(refetch, e => {
    if (['round_mismatch', 'choice_locked', 'battle_finished', 'invalid_target'].includes(e.code)) refetch()
    if (e.code === 'invalid_target') setDraft(d => ({ ...d, target: null }))
  })
  const me = b.actors.find(a => a.you) ?? null
  const foes = b.actors.filter(a => a.side !== b.your_side)
  const allies = b.actors.filter(a => a.side === b.your_side)
  const team = b.kind === 'team' || foes.length > 1 || allies.length > 1
  const [draft, setDraft] = useState<Draft>(() => loadDraft(b.id, b.round))
  const [targetSheet, setTargetSheet] = useState(false)
  const [confirmForfeit, setConfirmForfeit] = useState(false)
  const [fullLog, setFullLog] = useState(false)

  // A new round keeps only the target, and only while that foe is still standing.
  const standing = foes.filter(f => !f.out).map(f => f.id).join(',')
  useEffect(() => {
    setDraft(d => {
      const next = d.round === b.round ? d : loadDraft(b.id, b.round)
      const alive = next.target && standing.split(',').includes(next.target)
      return alive || !next.target ? next : { ...next, target: null }
    })
  }, [b.id, b.round, standing])
  useEffect(() => saveDraft(b.id, draft), [b.id, draft])
  useRefetchAt(b.deadline_at, refetch, 800)

  const finished = b.status !== 'active' || Boolean(b.result)
  const pending = choosing.isLoading || forfeiting.isLoading
  const locked = Boolean(b.my_choice) || Boolean(me?.out) || pending || finished
  const mc = b.my_choice
  const shown: Draft = mc
    ? { round: b.round, attack: mc.attack || null, defend: mc.defend || null, action: mc.action, target: mc.target || null, skill: mc.skill || null, defend2: mc.defend2 || null, ally: mc.ally || null }
    : draft
  const kit = me?.kit ?? []
  const skill = shown.action === 'skill' ? kit.find(k => k.id === shown.skill) ?? null : null
  const canOnslaught = (me?.focus ?? 0) >= ONSLAUGHT_FOCUS
  const canCharm = (me?.mn ?? 0) >= CHARM_MANA
  const blow = strikes(shown.action, skill)
  const resting = !blow
  const twoZones = Boolean(skill?.second_guard)
  const aimsFoe = blow || skill?.target === 'foe'
  const twilight = b.twilight_round > 0 && b.round >= b.twilight_round
  const target = team ? foes.find(f => f.id === shown.target) ?? null : foes[0] ?? null
  const ready = Boolean(shown.defend && (!twoZones || shown.defend2) && (!blow || shown.attack) && (!aimsFoe || !team || (target && !target.out)))

  const send = async () => {
    if (locked || sending.current || !ready || !shown.defend) return
    const extra = {
      ...(blow ? { attack: shown.attack! } : {}),
      ...(aimsFoe && team && target ? { target: target.id } : {}),
      ...(skill ? { skill: skill.id } : {}),
      ...(twoZones && shown.defend2 ? { defend2: shown.defend2 } : {}),
      ...(skill?.target === 'ally' && shown.ally ? { ally: shown.ally } : {}),
    }
    sending.current = true
    try { await cmd.run(() => choose({ id: b.id, round: b.round, defend: shown.defend!, action: shown.action, ...extra }).unwrap()) }
    finally { sending.current = false }
  }
  // A stance on two zones takes the second guarded zone from the next click on the guard.
  const setZone = (kind: 'attack' | 'defend', z: BlowZone) => !locked && setDraft(d => {
    if (kind === 'attack') return { ...d, attack: z }
    if (twoZones && d.defend && d.defend !== z) return d.defend2 === z ? { ...d, defend2: null } : { ...d, defend2: z }
    return { ...d, defend: z, defend2: d.defend2 === z ? null : d.defend2 }
  })
  // No blow (a breather, a sign without a blow): the attack zone is cleared and greys out.
  const setAction = (a: Action) => !locked && setDraft(d => ({ ...d, action: a, skill: null, defend2: null, attack: a === 'rest' ? null : d.attack }))
  const setSkill = (k: BattleSkill) => !locked && setDraft(d => ({
    ...d, action: 'skill', skill: k.id, defend2: k.second_guard ? d.defend2 : null,
    attack: k.blow === 'none' ? null : k.zone ? (k.zone as BlowZone) : d.attack,
  }))
  const setTarget = (id: string) => {
    if (locked) return
    setDraft(d => ({ ...d, target: id }))
    setTargetSheet(false)
  }

  const deadline = b.deadline_at ? countdown(now, b.deadline_at) : null
  const roundMs = useRoundLength(b.round, deadline?.ms ?? 0)

  let sealState: SealState = 'ready'
  let sealLabel = 'Подтвердить ход'
  let sealHint = 'выбор можно менять до отправки'
  if (finished) [sealState, sealLabel, sealHint] = ['disabled', 'Бой завершён', 'последний размен']
  else if (pending) [sealState, sealLabel, sealHint] = ['disabled', 'Отправляем…', 'подождите немного']
  else if (me?.out) [sealState, sealLabel, sealHint] = ['disabled', 'Вы выбыли', 'ждём конца боя']
  else if (b.my_choice) [sealState, sealLabel, sealHint] = ['disabled', 'Ход принят', foes.every(f => f.out || f.locked) ? 'раунд решается' : 'ждём соперника']
  else if (!ready) [sealState, sealHint] = ['disabled', twoZones && shown.defend && !shown.defend2 ? 'выберите вторую зону защиты' : resting ? 'выберите защиту' : team && !target ? 'выберите цель' : 'выберите удар и защиту']

  const choiceLine = (
    <div className="choice slab" aria-live="polite">
      {team && aimsFoe && <>Цель: <b>{target ? target.name : '—'}</b><i>◆</i></>}
      Удар: <b>{resting ? 'нет' : shown.attack ? ZONE_RU[shown.attack] : '—'}</b><i>◆</i>Защита: <b>{shown.defend ? ZONE_RU[shown.defend] : '—'}{twoZones && shown.defend2 ? ` и ${ZONE_RU[shown.defend2]}` : ''}</b><i>◆</i><b>{skill ? skill.name : ACTION_RU[shown.action]}</b>
    </div>
  )
  const actions = (
    <div className="actrow" role="radiogroup" aria-label="Действие раунда">
      <ActionTile id="strike" emblem="act.strike" name="Удар" cost="+◆" hint="+1 напор, +2 маны" on={shown.action === 'strike'} disabled={locked} onPick={setAction} />
      <ActionTile id="onslaught" emblem="act.onslaught" name="Натиск" cost={`◆${ONSLAUGHT_FOCUS}`} hint={canOnslaught ? `урон ×1,35 · ваша защита пропускает 50%` : `нужно ${ONSLAUGHT_FOCUS} напора`}
        on={shown.action === 'onslaught'} disabled={locked || !canOnslaught} onPick={setAction} />
      <ActionTile id="charm" emblem="act.charm" name="Памятный знак" cost={`${CHARM_MANA} маны`} hint={canCharm ? 'урон по вам ×0,8, оберег на раунд' : `нужно ${CHARM_MANA} маны`}
        on={shown.action === 'charm'} disabled={locked || !canCharm} onPick={setAction} tone="mana" />
      <ActionTile id="rest" emblem="act.rest" name="Передышка" cost="+◆◆" hint={`без удара · +${REST_FOCUS} напора${twilight ? '' : `, +${REST_MANA} маны`} · защита пропускает 15%`}
        on={shown.action === 'rest'} disabled={locked} onPick={setAction} />
    </div>
  )
  const kitRow = kit.length > 0 && (
    <div className="actrow actrow--kit" role="radiogroup" aria-label="Боевой набор">
      {kit.map(k => <SkillTile key={k.id} k={k} on={shown.action === 'skill' && shown.skill === k.id} locked={locked} onPick={setSkill} />)}
    </div>
  )
  const allyRow = skill?.target === 'ally' && allies.length > 1 && (
    <div className="allyrow" role="radiogroup" aria-label="На кого знак">
      {allies.filter(a => !a.out).map(a => (
        <button key={a.id} type="button" role="radio" aria-checked={(shown.ally ?? me?.id) === a.id} disabled={locked}
          className={cx('allybtn', (shown.ally ?? me?.id) === a.id && 'is-selected')} onClick={() => setDraft(d => ({ ...d, ally: a.id }))}>{a.you ? 'себе' : a.name}</button>
      ))}
    </div>
  )
  const actionNote = skill
    ? <p className="actnote" aria-live="polite"><b>{skill.name}</b> <small>({skillFacts(skill)})</small>: {skill.text}{twoZones ? ` Защита: ${shown.defend ? ZONE_RU[shown.defend] : '—'}, вторая зона: ${shown.defend2 ? ZONE_RU[shown.defend2] : 'нажмите ещё одну'}.` : ''}</p>
    : <p className="actnote" aria-live="polite"><b>{ACTION_RU[shown.action]}:</b> {ACTION_NOTE[shown.action](twilight)}</p>

  return (
    <div className={cx('battle battle--cinematic', team && 'battle--team', wide && 'battle--wide', kit.length > 0 && 'battle--kit', twilight && 'battle--twilight')}>
      <div className="roundplate">
        <div className="plank"><div className="plank__in">Раунд {b.round} <small>из {b.max_rounds}</small></div></div>
        {deadline && (
          <div className={cx('timer', deadline.ms < 10_000 && 'timer--low')} style={{ '--p': `${roundMs ? Math.round((deadline.ms / roundMs) * 100) : 100}%` } as CSSProperties}
            role="timer" aria-label={`До конца раунда ${deadline.text}`}><b>{deadline.ms > 0 ? deadline.text : '…'}</b></div>
        )}
        <Dusk round={b.round} from={b.twilight_round} />
        <div className={cx('netdot', connection === 'lost' && 'is-lost')}><span className="netdot__text">{connection === 'lost' ? 'нет связи' : 'связь есть'}</span></div>
      </div>
      <p className="visually-hidden" aria-live="polite">{playback.play ? `Размен ${playback.play.round.round}. Итоги доступны в журнале боя.` : b.my_choice ? 'Ход принят' : `Раунд ${b.round}`}{twilight ? '. Сумерки' : ''}</p>
      {failed && <div className="battle__warn" role="alert">Бой не обновился. <button type="button" className="linkbtn" onClick={refetch}>Повторить</button></div>}

      <div className="battle__visuals">
        <BattleScene battle={b} play={playback.play} reduced={reduced} target={target?.id ?? null} skip={playback.skip} openLog={() => setFullLog(true)}>
          <div className="battle__field" tabIndex={0} aria-label="Участники боя">
            <Side actors={allies} />
            <Foes foes={foes} team={team} target={target?.id ?? null} onTarget={setTarget} locked={locked} />
          </div>
        </BattleScene>
      </div>

      <section className="console iron" aria-label="Выбор хода">
        <header className="battle__plan-head">
          <h1>{b.my_choice ? 'Ход принят' : me?.out ? 'Вы выбыли' : finished ? 'Бой завершён' : 'Ваш ход'}</h1>
          {twilight && <span className="battle__twilight"><Emblem name="battle.twilight" size={20} />Сумерки</span>}
          <button type="button" className="linkbtn battle__forfeit" onClick={() => setConfirmForfeit(true)} disabled={Boolean(me?.out) || pending || finished}>Сдаться</button>
        </header>
        <div className="battle__inputs" tabIndex={0} aria-label="Параметры хода">
          {b.hint && <Coach hint={b.hint} />}
          <GroupRules battle={b} />
          <div className="ledger">
            <div className="battle__hotbar" aria-label="Действия и умения">{actions}{kitRow}</div>
            {actionNote}
            {team && aimsFoe && <PlankButton className="battle__pick" disabled={locked} onClick={() => setTargetSheet(true)}>Цель: {target ? target.name : 'выберите'} ▾</PlankButton>}
            {allyRow}
          </div>
          <div className="battle__zones">
            <ZoneGroup kind="attack" title="Удар" value={resting ? null : shown.attack} locked={locked || resting || Boolean(skill?.zone)} off={resting} onPick={z => setZone('attack', z)} />
            <ZoneGroup kind="defend" title="Защита" value={shown.defend} second={twoZones ? shown.defend2 : null} locked={locked} onPick={z => setZone('defend', z)} />
          </div>
          {foes.some(a => a.telegraph) && <div className="battle__observations">
            {foes.filter(a => a.telegraph).map(a => <div key={a.id} className="telegraph parch"><b>Наблюдение — {a.name}:</b> {a.telegraph}</div>)}
          </div>}
        </div>
        <div className="battle__footer">
          {cmd.text && <p className="battle__error" role="alert">{cmd.text}</p>}
          {choiceLine}
          <span className="battle__seal-hint">{sealHint}</span>
          {twilight && <span className="battle__twilight-rules">Удары +{TWILIGHT_PCT}% · лечение вдвое слабее</span>}
          <div className="battle__submit"><SealBar emblem="battle.confirm" label={sealLabel} state={sealState} onPress={send} /></div>
        </div>
      </section>

      <Sheet open={targetSheet} title="Цель" emblem="battle.target" onClose={() => setTargetSheet(false)}>
        <ul className="plist">
          {foes.map(f => (
            <li key={f.id}>
              <button type="button" className={cx('prow', target?.id === f.id && 'is-selected', f.out && 'is-out')} disabled={f.out || locked} onClick={() => setTarget(f.id)}>
                <Emblem name={f.npc ? 'battle.foe' : 'nav.hero'} /><span className="name">{f.name}<small>ур. {f.level}{f.out ? ' · выбыл' : ''}</small></span>
                <span className="mod">{f.hp}/{f.hp_max}</span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
      <Sheet open={confirmForfeit} title="Сдаться?" emblem="battle.surrender" onClose={() => setConfirmForfeit(false)}>
        <p className="note battle__confirm">Бой закончится поражением{b.rated ? ', рейтинг изменится' : ''}. Награды не будет.</p>
        <div className="actions">
          <PlankButton gold disabled={pending || finished} onClick={async () => {
            if (sending.current || pending || finished) return
            sending.current = true
            try { if (await cmd.run(() => forfeit({ id: b.id }).unwrap())) setConfirmForfeit(false) }
            finally { sending.current = false }
          }}>Сдаться</PlankButton>
          <PlankButton onClick={() => setConfirmForfeit(false)}>Биться дальше</PlankButton>
        </div>
      </Sheet>
      <Sheet open={fullLog} title="Журнал боя" emblem="nav.journal" onClose={() => setFullLog(false)}>
        <FullLog battle={b} />
      </Sheet>
    </div>
  )
}

/** The longest time seen for this round stands for its length (the timer ring). */
function useRoundLength(round: number, ms: number): number {
  const seen = useRef<{ round: number; max: number }>({ round, max: ms })
  if (seen.current.round !== round) seen.current = { round, max: ms }
  if (ms > seen.current.max) seen.current.max = ms
  return seen.current.max
}

function Focus({ value, max, label }: { value: number; max: number; label?: string }) {
  return (
    <div className="focus" aria-label={`Напор ${value} из ${max}`}>
      <span>{label ?? 'Напор'}</span>
      <div className="pips" aria-hidden="true">{Array.from({ length: max }, (_, i) => <span key={i} className={cx('pip', i < value && 'is-on')} />)}</div><b>{value}<small> / {max}</small></b>
    </div>
  )
}

/** The coach of a lesson speaks before every round (tutorial battles). */
function Coach({ hint }: { hint: BattleHint }) {
  return (
    <aside className="coach parch" aria-label={`Совет: ${hint.name}`}>
      <span className="coach__face">{hint.portrait ? <img src={hint.portrait} alt="" /> : <Emblem name="nav.hero" size={40} />}</span>
      <p><b>{hint.name}:</b> {hint.text}</p>
    </aside>
  )
}

/** Twilight (mezha-2): from its round every blow is stronger; the plate warns ahead. */
function Dusk({ round, from }: { round: number; from: number }) {
  if (!from) return null
  const left = from - round
  const text = left <= 0 ? `Сумерки: удары +${TWILIGHT_PCT}%` : `Сумерки через ${left} ${left === 1 ? 'раунд' : left < 5 ? 'раунда' : 'раундов'}`
  return (
    <div className={cx('dusk', left <= 0 && 'is-on')} title={`С ${from}-го раунда: урон +${TWILIGHT_PCT}%, лечение вдвое слабее, передышка без маны`} aria-label={text}>
      <Emblem name="battle.twilight" size={18} /><span>{text}</span>
    </div>
  )
}

/** Signs of the statuses a fighter can carry (the rest await their art: art_prompts_ui.md, «Статусы»). */
/** Every status of the battle has its sign (art_prompts_skills.md §1.2). */
const STATUS_EMBLEM = (id: string): EmblemName | null => (id in STATUS_RU ? (`status.${id}` as EmblemName) : null)

function StatusChip({ s }: { s: BattleStatus }) {
  const name = STATUS_RU[s.id] ?? s.id
  const em = STATUS_EMBLEM(s.id)
  const left = s.rounds > 0 ? `ещё ${roundsText(s.rounds)}` : 'до срабатывания'
  return <CombatTooltip className={cx('stchip', s.good ? 'stchip--good' : 'stchip--bad')} name={name} description={`${STATUS_TEXT[s.id] ?? name} (${left})`}>
    {() => <>{em && <Emblem name={em} size={14} />}<span className="stchip__name">{name}</span>{s.rounds > 0 && <b>{s.rounds}</b>}</>}
  </CombatTooltip>
}

function Fighter({ a, foe, compact, nameControl }: { a: BattleActor; foe?: boolean; compact?: boolean; nameControl?: ReactNode }) {
  return (
    <div className={cx('fighter', foe ? 'fighter--foe' : 'fighter--me', compact && 'fighter--compact', a.out && 'is-out')}>
      <div className="fighter__portrait">{a.portrait ? <img src={a.portrait} alt="" /> : <Emblem name={a.npc ? 'battle.foe' : 'nav.hero'} size={compact ? 52 : 90} />}</div>
      <div className={cx('plank', !foe && 'plank--gold')}>{nameControl ?? <div className="plank__in">{a.name} <small>· ур. {a.level}</small></div>}</div>
      <BattleResource kind="hp" actor={a} value={a.hp} max={a.hp_max} />
      {a.mn_max > 0 && <BattleResource kind="mn" actor={a} value={a.mn} max={a.mn_max} />}
      <Focus value={a.focus} max={a.focus_max} />
      <div className="fighter__status">
        {a.statuses.map(s => <StatusChip key={s.id} s={s} />)}
        {a.ward && <CombatTooltip className="stchip stchip--good" name="Оберег" description="Защищает от первого наложения статуса.">{() => <><Emblem name="status.ward" size={14} /><span className="stchip__name">оберег</span></>}</CombatTooltip>}
        {a.out && <span className="tagline tagline--bad">выбыл</span>}
        {!a.out && !a.you && (a.locked ? <span className="ready">ход сделан</span> : <span className="ready is-waiting">выбирает…</span>)}
      </div>
    </div>
  )
}

function BattleResource({ kind, actor, value, max }: { kind: 'hp' | 'mn'; actor: BattleActor; value: number; max: number }) {
  const name = kind === 'hp' ? 'Здоровье' : 'Мана'
  return <CombatTooltip className="combat-resource" name={name} description={`${actor.name}: ${value} из ${max}`}>
    {() => <Gauge kind={kind} value={value} max={max} label={`${name}: ${actor.name}`} showIcon={false} />}
  </CombatTooltip>
}

function Side({ actors }: { actors: BattleActor[] }) {
  return <div className="side side--mine side--team">{actors.map(a => <Fighter key={a.id} a={a} compact />)}</div>
}

/** Opponents; in a team fight they are the targets: click, arrows and Enter. */
function Foes({ foes, team, target, onTarget, locked }: { foes: BattleActor[]; team: boolean; target: string | null; onTarget: (id: string) => void; locked: boolean }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const alive = useMemo(() => foes.map((f, i) => ({ f, i })).filter(x => !x.f.out), [foes])
  if (!team) return <div className="side side--foes">{foes.map(a => <Fighter key={a.id} a={a} foe compact />)}</div>
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!d || alive.length === 0) return
    e.preventDefault()
    const pos = alive.findIndex(x => x.i === i)
    const next = alive[(pos + d + alive.length) % alive.length]
    refs.current[next.i]?.focus()
  }
  return (
    <div className="side side--foes side--team" role="radiogroup" aria-label="Цель удара">
      {foes.map((a, i) => (
        <div className="target-card" key={a.id}><Fighter a={a} foe compact nameControl={<button ref={el => { refs.current[i] = el }} type="button" role="radio" aria-checked={target === a.id} disabled={a.out || locked}
          tabIndex={target === a.id || (!target && alive[0]?.i === i) ? 0 : -1}
          className={cx('target', target === a.id && 'is-target')} onClick={() => onTarget(a.id)} onKeyDown={e => onKey(e, i)}
          aria-label={`${a.name}, ур. ${a.level}, здоровье ${a.hp} из ${a.hp_max}${a.out ? ', выбыл' : ''}`}>
          <span className="plank__in">{a.name}<small>· ур. {a.level}{a.out ? ' · выбыл' : target === a.id ? ' · цель' : ''}</small></span>
        </button>} /></div>
      ))}
    </div>
  )
}

function ZoneGroup({ kind, title, value, second, locked, off, onPick }: {
  kind: 'attack' | 'defend'; title: string; value: BlowZone | null; second?: BlowZone | null; locked: boolean; off?: boolean; onPick: (z: BlowZone) => void
}) {
  return (
    <div className={cx(`zgroup zgroup--${kind}`, off && 'is-off')} role="radiogroup" aria-label={off ? `${title}: передышка, без удара` : title}>
      <div className="zgroup__title" aria-hidden="true">{title.toUpperCase()}</div>
      <div className="zrow">
        {ZONES.map(z => (
          <button key={z} type="button" role="radio" aria-checked={value === z || second === z} disabled={locked}
            className={cx('zbtn', value === z && 'is-selected', second === z && 'is-second', locked && value !== z && second !== z && 'is-dim')} onClick={() => onPick(z)}>
            <span className="zbtn__face"><Emblem name={`zone.${z}` as EmblemName} /></span>{ZONE_TITLE[z]}
            {second === z && <small className="zbtn__note">вторая</small>}
          </button>
        ))}
      </div>
    </div>
  )
}

/** One action of the round: its price or gain on a tag, what it does under the name. */
function ActionTile({ id, emblem, name, cost, hint, on, disabled, tone, onPick }: {
  id: Action; emblem: EmblemName; name: string; cost: string; hint: string; on: boolean; disabled: boolean; tone?: 'mana'; onPick: (a: Action) => void
}) {
  return <CombatAction name={name} description={hint} emblem={emblem} cost={cost} selected={on} disabled={disabled} mana={tone === 'mana'} onPick={() => onPick(id)} />
}

/** A skill of the combat set: its price, the rounds of its cooldown, why it cannot be chosen. */
function SkillTile({ k, on, locked, onPick }: { k: BattleSkill; on: boolean; locked: boolean; onPick: (k: BattleSkill) => void }) {
  const why = k.block === 'cooldown' ? `ещё ${roundsText(k.cooldown)}` : k.block ? BLOCK_RU[k.block] ?? k.block : ''
  const emblem: EmblemName = k.id === 'spear.trip' ? 'status.trip' : k.id === 'sign.seal' ? 'status.seal' : KIND_EMBLEM[k.kind]
  return <CombatAction name={k.name} description={`${k.text} · ${skillFacts(k)}${why ? ` · Недоступно: ${why}` : ''}`} emblem={emblem}
    cost={costText(k.cost_focus, k.cost_mana)} cooldown={k.block === 'cooldown' ? k.cooldown : undefined}
    selected={on} disabled={locked || Boolean(k.block)} mana={k.cost_mana > 0 && !k.cost_focus} onPick={() => onPick(k)} />
}
